import React from 'react';
import { cn } from '@/lib/utils';

export interface ReactionType {
  id: string;
  emoji: string;
  label: string;
}

export const REACTION_TYPES: ReactionType[] = [
  { id: 'fire', emoji: '🔥', label: 'Hype' },
  { id: 'trophy', emoji: '🏆', label: 'Elite Play' },
  { id: 'grind', emoji: '💪', label: 'Hard Work' },
  { id: 'brain', emoji: '🧠', label: 'Smart Play' },
  { id: 'flowers', emoji: '🌸', label: 'Respect' },
  { id: 'teammate', emoji: '🤝', label: 'Great Assist' },
  { id: 'clutch', emoji: '🎯', label: 'Clutch' },
  { id: 'lockdown', emoji: '🧱', label: 'Lockdown' },
  { id: 'levelup', emoji: '🚀', label: 'Level Up' },
];

interface AllowanceInfo {
  remaining: number;
  weeklyLimit: number;
}

interface ReactionPickerProps {
  onSelect: (reactionType: string) => void;
  onClose: () => void;
  allowances?: Record<string, AllowanceInfo>;
}

const ReactionPicker: React.FC<ReactionPickerProps> = ({ onSelect, onClose, allowances }) => {
  const getRemainingText = (reactionId: string): string | null => {
    if (!allowances) return null;
    const allowance = allowances[reactionId];
    if (!allowance) return null;
    if (allowance.weeklyLimit === -1) return '∞';
    return `${allowance.remaining}`;
  };

  const isDisabled = (reactionId: string): boolean => {
    if (!allowances) return false;
    const allowance = allowances[reactionId];
    if (!allowance) return false;
    if (allowance.weeklyLimit === -1) return false;
    return allowance.remaining <= 0;
  };

  return (
    <div 
      className="absolute bottom-full left-0 mb-2 bg-background border-2 rounded-3xl shadow-2xl p-6 z-50"
      onMouseLeave={onClose}
    >
      <div className="grid grid-cols-3 gap-4">
        {REACTION_TYPES.map((reaction) => {
          const disabled = isDisabled(reaction.id);
          const remainingText = getRemainingText(reaction.id);
          
          return (
            <button
              key={reaction.id}
              onClick={() => !disabled && onSelect(reaction.id)}
              disabled={disabled}
              className={cn(
                'relative text-5xl p-4 rounded-2xl transition-all',
                'focus:outline-none focus:ring-2 focus:ring-primary',
                disabled 
                  ? 'opacity-40 cursor-not-allowed grayscale' 
                  : 'hover:scale-110 hover:bg-accent'
              )}
              title={disabled ? `No ${reaction.label} left this week` : reaction.label}
            >
              {reaction.emoji}
              {remainingText && (
                <span 
                  className={cn(
                    'absolute -bottom-1 -right-1 text-sm font-bold rounded-full px-2 min-w-[24px]',
                    remainingText === '∞' 
                      ? 'text-muted-foreground' 
                      : remainingText === '0'
                        ? 'bg-destructive/20 text-destructive'
                        : 'bg-primary/20 text-primary'
                  )}
                >
                  {remainingText}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <p className="text-sm text-muted-foreground text-center mt-3 border-t pt-3">
        🔥 unlimited • others reset Monday
      </p>
    </div>
  );
};

export default ReactionPicker;
